import { useState, useRef, useCallback } from 'react'
import { RefreshCw } from 'lucide-react'

/**
 * Componente Pull-to-Refresh per mobile
 * 
 * @param {Function} onRefresh - Funzione async da chiamare al refresh
 * @param {React.ReactNode} children - Contenuto
 */
export function PullToRefresh({ onRefresh, children, className = '' }) {
  const [pulling, setPulling] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const containerRef = useRef(null)
  const startY = useRef(0)
  const threshold = 80 // Distanza minima per attivare refresh

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY
      setPulling(true)
    }
  }, [])

  const handleTouchMove = useCallback((e) => {
    if (!pulling || refreshing) return

    const currentY = e.touches[0].clientY
    const distance = Math.max(0, currentY - startY.current)
    
    // Resistenza progressiva
    const resistance = Math.min(distance * 0.5, 120)
    setPullDistance(resistance)
  }, [pulling, refreshing])

  const handleTouchEnd = useCallback(async () => {
    if (!pulling) return

    if (pullDistance >= threshold && !refreshing) {
      setRefreshing(true)
      setPullDistance(50) // Mantieni indicatore visibile
      
      try {
        await onRefresh?.()
      } catch (e) {
        console.error('Refresh error:', e)
      }
      
      setRefreshing(false)
    }
    
    setPullDistance(0)
    setPulling(false)
  }, [pulling, pullDistance, refreshing, onRefresh])

  return (
    <div
      ref={containerRef}
      className={`relative overflow-auto ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Indicatore pull */}
      <div
        className="absolute left-0 right-0 flex justify-center transition-transform duration-200"
        style={{
          transform: `translateY(${pullDistance - 50}px)`,
          opacity: Math.min(pullDistance / threshold, 1)
        }}
      >
        <div className={`
          p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg
          ${refreshing ? 'animate-spin' : ''}
        `}>
          <RefreshCw 
            className={`h-6 w-6 text-primary-600 transition-transform duration-200`}
            style={{
              transform: refreshing ? 'none' : `rotate(${pullDistance * 2}deg)`
            }}
          />
        </div>
      </div>

      {/* Contenuto */}
      <div
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: pulling ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default PullToRefresh
